import React, { useEffect, useState } from 'react';
import { FaArrowRight, FaChevronLeft, FaChevronRight, FaGamepad } from 'react-icons/fa';
import GameCard from './GameCard';
import Joyride from 'react-joyride';

const games = [
    { title: 'Tetris', image: '/tetris.png' },
    { title: 'Meteor Blasters', image: '/MeteorBlaster.png' },
    { title: '2048', image: '/2048game.png' },
    { title: 'Match Gamee', image: '/MatchingGame.jpg' },
    { title: 'Whack A Mole', image: '/whack.png' },
    { title: 'Snake', image: '/snake-game.png' }
];

const Dashboard = ({ isLightMode }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [runTour, setRunTour] = useState(false);
    const [controllerAnimate, setControllerAnimate] = useState(false);

    // Effect to slideshow the games every 5 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 4) % games.length);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    // Add subtle controller animation
    useEffect(() => {
        const animationInterval = setInterval(() => {
            setControllerAnimate(true);
            setTimeout(() => setControllerAnimate(false), 1000);
        }, 3000);

        return () => clearInterval(animationInterval);
    }, []);

    const displayedGames = games.slice(currentIndex, currentIndex + 4).concat(games.slice(0, Math.max(0, (currentIndex + 4) - games.length)));

    const nextGames = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 4) % games.length);
    };

    const prevGames = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 4 + games.length) % games.length);
    };

    // Steps for the onboarding tour
    const tourSteps = [
        {
            target: '.get-started-button',
            content: 'Click here to start your journey!',
        },
        {
            target: '.games-box',
            content: 'This is the games area where you can view the games!',
        },
        {
            target: '.games-page-link',
            content: 'Click here to view all available games in the arcade!',
        },
    ];

    return (
        <div className="p-4 md:p-8 text-[var(--text-color)] bg-[var(--dashboard-background-color)] flex flex-col min-h-screen">
            <Joyride
                steps={tourSteps}
                run={runTour}
                continuous
                showSkipButton
                showProgress
                styles={{
                    options: {
                        zIndex: 1000,
                        primaryColor: isLightMode ? '#0056B3' : '#4A6FA5',
                        textColor: isLightMode ? '#212529' : '#FFFFFF',
                        backgroundColor: isLightMode ? '#FFFFFF' : '#121212',
                    },
                }}
                callback={(data) => {
                    if (data.status === 'finished' || data.status === 'skipped') {
                        setRunTour(false);
                    }
                }}
            />
            
            {/* Dashboard Header */}
            <header className="mb-10 md:mb-14">
                {/* Remove inline style and use className for better consistency */}
                <h1 className={`text-center ${isLightMode ? 'text-[var(--primary-color)]' : 'text-[var(--dashboard-title-color)]'} text-5xl md:text-6xl mb-4 font-['Jersey_25',sans-serif] drop-shadow-lg tracking-wide`}>
                    Welcome to Quiz Arcade!
                </h1>
                <p className="text-center mx-auto max-w-3xl text-[var(--intro-text-color)] opacity-90 text-lg md:text-xl">
                    Learn, play, and compete in our accessible gaming experience.
                </p>
            </header>
            
            {/* Hero Section */}
            <section className="flex flex-col md:flex-row justify-between items-center gap-8 md:gap-12 mb-20">
                {/* Game Controller Image with Background and Animation */}
                <div className="relative w-full md:w-1/2 flex justify-center">
                    <div className="absolute w-[350px] h-[350px] md:w-[450px] md:h-[450px] rounded-full bg-[var(--primary-color)] opacity-10 blur-xl"></div>
                    <img
                        src="/controller.png"
                        alt="Game Controller"
                        className={`w-[500px] md:w-[600px] relative z-10 drop-shadow-xl transition-transform duration-1000 ease-in-out ${
                            controllerAnimate ? 'scale-105 rotate-1' : 'scale-100 rotate-0'
                        }`}
                    />
                </div>
                
                {/* Introduction Text with Card Design */}
                <div className={`w-full md:w-1/2 ${isLightMode ? 'bg-[var(--background-color)] shadow-md border border-[var(--border-color)]' : 'bg-[var(--surface-color)] border border-[rgba(255,255,255,0.1)]'} p-8 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl`}>
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[var(--primary-color)] mr-4">
                            <FaGamepad className="text-white text-xl" aria-hidden="true" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-[var(--intro-text-color)]">
                            Game & Learn
                        </h2>
                    </div>
                    
                    <p className="text-[var(--intro-text-color)] text-lg md:text-xl leading-relaxed mb-8 border-l-4 border-[var(--primary-color)] pl-4 italic">
                        Dive into exciting games that not only challenge your skills but also quiz you on your knowledge. Rack up points, compete globally, and track your progress on our leaderboards.
                    </p>
                    
                    {/* Feature Points with Enhanced Style and Fixed Contrast */}
                    <div className={`mb-8 ${isLightMode ? 'bg-[#F5F5F5]' : 'bg-[rgba(0,0,0,0.2)]'} p-4 rounded-lg`}>
                        <h3 className="text-xl font-medium mb-3 text-[var(--intro-text-color)]">
                            Key Features:
                        </h3>
                        <ul className="space-y-3">
                            <li className="flex items-center text-[var(--intro-text-color)] text-lg">
                                <span className="inline-block min-w-3 h-3 rounded-full bg-[var(--interactive-color)] mr-4"></span>
                                <span>Play <strong>interactive games</strong> with accessibility in mind</span>
                            </li>
                            <li className="flex items-center text-[var(--intro-text-color)] text-lg">
                                <span className="inline-block min-w-3 h-3 rounded-full bg-[var(--interactive-color)] mr-4"></span>
                                <span>Answer <strong>quizzes</strong> to earn points and learn</span>
                            </li>
                            <li className="flex items-center text-[var(--intro-text-color)] text-lg">
                                <span className="inline-block min-w-3 h-3 rounded-full bg-[var(--interactive-color)] mr-4"></span>
                                <span>Compete on <strong>global leaderboards</strong> with others</span>
                            </li>
                        </ul>
                    </div>
                    
                    {/* Get Started Button with Consistent Color Usage */}
                    <div className="flex justify-center md:justify-start">
                        <button
                            className="get-started-button bg-[var(--interactive-color)] hover:brightness-110 text-white rounded-lg py-3.5 px-8 text-xl w-full md:w-auto font-medium transition-all duration-300 shadow-md focus:outline focus:outline-[var(--focus-outline-color)] focus:outline-offset-2 flex items-center justify-center gap-2"
                            onClick={() => setRunTour(true)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setRunTour(true);
                                }
                            }}
                        >
                            Get Started <FaArrowRight className="inline-block" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </section>
            
            {/* Games Box */}
            <section className="games-box w-full max-w-5xl mx-auto bg-[var(--surface-color)] pt-10 pb-12 rounded-xl shadow-xl overflow-hidden border border-[var(--border-color)]">
                <h2 className="text-[var(--text-color)] text-center text-4xl md:text-5xl mb-10 font-bold">
                    Quiz While You Play
                </h2>
                
                {/* Games Navigation */}
                <div className="flex items-center justify-between px-6 mb-2">
                    {/* Previous Button */}
                    <button 
                        className="w-12 h-12 flex items-center justify-center bg-[var(--interactive-color)] text-white border-none rounded-full cursor-pointer transition-colors duration-300 hover:brightness-110 focus:outline focus:outline-[var(--focus-outline-color)]"
                        onClick={prevGames}
                        aria-label="View previous games"
                    >
                        <FaChevronLeft className="text-xl" />
                    </button>
                    
                    {/* Games Container */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 px-4">
                        {displayedGames.map((game, index) => (
                            <GameCard key={index} title={game.title} image={game.image} />
                        ))}
                    </div>
                    
                    {/* Next Button */}
                    <button 
                        className="w-12 h-12 flex items-center justify-center bg-[var(--interactive-color)] text-white border-none rounded-full cursor-pointer transition-colors duration-300 hover:brightness-110 focus:outline focus:outline-[var(--focus-outline-color)]"
                        onClick={nextGames}
                        aria-label="View next games"
                    >
                        <FaChevronRight className="text-xl" />
                    </button>
                </div>
                
                {/* Game Indicators */}
                <div className="flex justify-center mt-8 space-x-3">
                    {games.map((_, idx) => (
                        <button
                            key={idx}
                            className={`w-3 h-3 rounded-full transition-colors duration-300 focus:outline focus:outline-[var(--focus-outline-color)] ${
                                idx >= currentIndex && idx < currentIndex + 4 
                                    ? 'bg-[var(--interactive-color)]' 
                                    : isLightMode ? 'bg-gray-300' : 'bg-gray-600'
                            }`}
                            onClick={() => setCurrentIndex(idx)}
                            aria-label={`Navigate to game set ${idx + 1}`}
                        />
                    ))}
                </div>
                
                {/* View All Games Link */}
                <div className="text-center mt-10">
                    <a 
                        href="/games" 
                        className="games-page-link inline-block bg-[var(--interactive-color)] text-white py-3 px-8 rounded-lg text-xl transition-all duration-300 hover:brightness-110 shadow-md focus:outline focus:outline-[var(--focus-outline-color)] no-underline"
                        aria-label="View all available games"
                    >
                        All Games <FaArrowRight className="inline-block ml-2" aria-hidden="true" />
                    </a>
                </div>
            </section>
        </div>
    );
};

export default Dashboard;